import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography,
    Tooltip,
    IconButton,
    Alert,
    Chip,
    Link
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LabelTemplateViewModel } from "../../Types/ExportTypes";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
    ItemLabelBarcodeTypeAutoComplete, LanguageAutoComplete
} from "../AutoComplete/AutoCompletes";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

interface LabelTemplateDialogProps {
    open: boolean;
    onClose: () => void;
}

const createEmptyTemplate = (): LabelTemplateViewModel => ({
    id: 0,
    description: "",
    initCode: "",
    template: "",
    languageCode: "",
    barcodeType: ""
});

const LabelTemplateDialog: React.FC<LabelTemplateDialogProps> = ({ open, onClose }) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading,showThinking,hideThinking } = useLoading();
    const { t } = useTranslation();

    const [templates, setTemplates] = useState<LabelTemplateViewModel[]>([]);
    const [editingTemplate, setEditingTemplate] = useState<LabelTemplateViewModel | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [suggestIsOpen, setSuggestIsOpen] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [variablesOpen, setVariablesOpen] = useState(false);

    const variableGroups = useMemo(() => ({
        general: [
            { placeholder: "{0}", label: t("common.description", { defaultValue: "Description" }) },
            { placeholder: "{1}", label: t("common.itemNo", { defaultValue: "Item No" }) },
            { placeholder: "{2}", label: t("common.colour", { defaultValue: "Color" }) },
            { placeholder: "{3}", label: t("common.size", { defaultValue: "Size" }) },
            { placeholder: "{4}", label: t("common.length", { defaultValue: "Length" }) },
            { placeholder: "{5}", label: t("common.barcode", { defaultValue: "Barcode" }) },
            { placeholder: "{6}", label: t("common.price", { defaultValue: "Price" }) },
            { placeholder: "{7}", label: t("common.currency", { defaultValue: "Currency" }) },
            { placeholder: "{8}", label: t("common.supplierItemNo", { defaultValue: "Supplier Item No" }) },
            { placeholder: "{9}", label: t("common.shelf", { defaultValue: "Shelf" }) },
            { placeholder: "{10}", label: t("common.composition", { defaultValue: "Composition" }) }
        ],
        extended: [
            { placeholder: "{20}", label: t("common.itemGroup", { defaultValue: "Item Group" }) },
            { placeholder: "{21}", label: t("common.itemCategory", { defaultValue: "Item Category" }) },
            { placeholder: "{22}", label: t("common.productGroup", { defaultValue: "Product Group" }) },
            { placeholder: "{23}", label: t("common.program", { defaultValue: "Program" }) },
            { placeholder: "{24}", label: t("common.collection", { defaultValue: "Collection" }) },
            { placeholder: "{25}", label: t("common.baseColour", { defaultValue: "Base Color Code" }) },
            { placeholder: "{26}", label: t("common.colour", { defaultValue: "Color Code" }) },
            { placeholder: "{27}", label: t("common.priceGroup", { defaultValue: "Price Group" }) },
            { placeholder: "{28}", label: t("common.originalUnitPrice", { defaultValue: "Original Unit Price" }) },
            { placeholder: "{bc}", label: t("common.barcode", { defaultValue: "Barcode" }) },
            { placeholder: "{n}", label: t("common.quantity", { defaultValue: "Quantity" }) }
        ],
        pga: ["{PGA1}", "{PGA2}", "{PGA3}", "{PGA4}", "{PGA5}", "{PGB1}", "{PGB2}", "{PGB3}", "{PGB4}", "{PGB5}"]
    }), [t]);
    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authService.get<{ items: LabelTemplateViewModel[] }>("/labelJournalApi/GetTemplates");
            setTemplates(response.items ?? []);
        } catch (error: any) {
            showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    }, [authService, showSnackbar, t]);

    useEffect(() => {
        if (open) {
            fetchTemplates();
        }
    }, [open, fetchTemplates]);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const openCreateDialog = () => {
        setEditingTemplate(createEmptyTemplate());
        setEditOpen(true);
    };

    const openEditDialog = (template: LabelTemplateViewModel) => {
        setEditingTemplate({ ...template });
        setEditOpen(true);
    };

    const closeEditDialog = () => {
        setEditOpen(false);
        setEditingTemplate(null);
    };


    const openSuggestDialog = () => {
        setSuggestIsOpen(true);
    };

    const closeSuggestDialog = () => {
        setSuggestIsOpen(false);
        setPrompt("");
    };

    const openVariablesDialog = () => setVariablesOpen(true);

    const closeVariablesDialog = () => setVariablesOpen(false);

    const saveTemplate = async () => {
        if (!editingTemplate) return;
        try {
            await authService.post("/labelJournalApi/SaveTemplate", editingTemplate);
            closeEditDialog();
            fetchTemplates();
            showSnackbar(t("common.changesSaved"), "success");
            showLoading();
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const getPrompt = async () => {
        if (!prompt) {
            showSnackbar(t("dialog.missingPrompt"), "warning");
            return;
        } 
        try {
            showThinking();
            var response = await authService.post<LabelTemplateViewModel>("/labelJournalApi/SuggestTemplate", { languageCode: "sv", prompt, LabelTemplate: editingTemplate  });
            closeSuggestDialog();
            setEditingTemplate({ ...response, hasChanges:true });
            setEditOpen(true);
            showSnackbar(t("common.success"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideThinking();
        }
    };

    const testLabel = async () => {
        try {
            showLoading();
            await authService.post("/labelJournalApi/TestLabel", editingTemplate);
            showSnackbar(t("dialog.queuedForPrint"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };



    const deleteTemplate = async (template: LabelTemplateViewModel) => {
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        if (!template.id) return;
        try {
            showLoading();
            await authService.post(`/labelJournalApi/DeleteTemplate?id=${template.id}`, {});
            fetchTemplates();
            showSnackbar(t("common.deleted"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.deleteError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };




    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>{t("common.templates")}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Box sx={{}}>
                        <Button variant="contained" onClick={openCreateDialog} fullWidth>
                            {t("common.create")}
                        </Button>
                        <Button variant="contained" color="info" onClick={openSuggestDialog} fullWidth sx={{mt:1} }>
                            {t("common.labelSuggestAi")}
                        </Button>
                    </Box>
                    <Grid container spacing={2}>
                        {templates.map((template) => (
                            <Grid item xs={12} md={6} key={template.id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography variant="h6">{template.description}</Typography>
                                            {template.languageCode && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.language")}: {template.languageCode}
                                                </Typography>
                                            )}
                                            {template.barcodeType && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.barcode")}: {template.barcodeType}
                                                </Typography>
                                            )}
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>


                                                <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Tooltip title={t("common.edit")}>
                                                        <IconButton color="secondary" onClick={() => openEditDialog(template)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>

                                                <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            color="error"
                                                            onClick={async () => {
                                                                await deleteTemplate(template);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>




                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {!isLoading && templates.length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {t("common.noData")}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("common.close")}</Button>
            </DialogActions>

            <Dialog open={editOpen} onClose={closeEditDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editingTemplate?.id ? t("common.edit") : t("common.create")}


                    {/* Top-right save + unsaved chip */}
                    <Box sx={{ position: "absolute", top: 8, right: 12, display: "flex", gap: 1, alignItems: "center" }}>
                        {!!editingTemplate?.hasChanges && (
                            <Chip
                                size="small"
                                color="warning"
                                icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={t("common.unsavedChanges") ?? "Unsaved changes"}
                                sx={{
                                    "@keyframes breathe": {
                                        "0%": { opacity: 1 },
                                        "50%": { opacity: 0.75 },
                                        "100%": { opacity: 1 },
                                    },
                                    animation: "breathe 2.5s ease-in-out infinite",
                                }}
                            />
                        )}

                    
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1}}>
                        <TextField
                            label={t("common.description")}
                            value={editingTemplate?.description ?? ""}
                            onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, description: e.target.value, hasChanges: true } : prev)}
                            fullWidth
                        />


                        <LanguageAutoComplete
                            key={editingTemplate?.languageCode || null}
                            valueId={editingTemplate?.languageCode || null}
                            onChange={(selected) => setEditingTemplate(prev => prev ? { ...prev, languageCode: selected?.value, hasChanges: true } : prev)}
                        />


                        <ItemLabelBarcodeTypeAutoComplete
                            key={editingTemplate?.barcodeType || null}
                            valueId={editingTemplate?.barcodeType || null}
                            onChange={(selected) => setEditingTemplate(prev => prev ? { ...prev, barcodeType: selected?.value, hasChanges: true } : prev)}
                        />


                        <TextField
                            label={t("common.initCode")}
                            value={editingTemplate?.initCode ?? ""}
                            onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, initCode: e.target.value, hasChanges: true } : prev)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label={t("common.template")}
                            value={editingTemplate?.template ?? ""}
                            onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, template: e.target.value, hasChanges:true } : prev)}
                            fullWidth
                            multiline
                            minRows={6}
                        />
                 
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ width: "100%", p: 2 }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        sx={{ width: "100%" }}
                    >
                        <Button
                            variant="contained"
                            onClick={openVariablesDialog}
                            color="info"
                            fullWidth
                        >
                            {t("common.variables", { defaultValue: "Variables" })}
                        </Button>

                        <Button onClick={openSuggestDialog} variant="contained" fullWidth>
                            {t("common.labelChangeAi")}
                        </Button>

                        <Button onClick={testLabel} variant="contained" fullWidth>
                            {t("common.printTestLabel")}
                        </Button>

                        <Button onClick={closeEditDialog} fullWidth>
                            {t("common.cancel")}
                        </Button>

                        <Button onClick={saveTemplate} color="success" variant="contained" fullWidth>
                            {t("common.save")}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <Dialog open={suggestIsOpen} onClose={closeSuggestDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editingTemplate != null ? t("common.labelChangeAi") :  t("common.labelSuggestAi")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            {t("help.suggestAiLabelHelp")}
                        </Alert>
                        <TextField
                            label={t("common.request")}
                            value={prompt ?? ""}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setPrompt(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                       
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSuggestDialog}>{t("common.cancel")}</Button>
                    <Button onClick={getPrompt} color="success" variant="contained">{t("common.run")}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={variablesOpen} onClose={closeVariablesDialog} maxWidth="md" fullWidth>
                <DialogTitle>{t("common.variables")}</DialogTitle>
                <DialogContent>
                    <Box component="div" sx={{ mt: 1 }}>
       
                        <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} />
                        <Link href="/assets/EPL2en.pdf" download underline="always">
                                EPL2 Programmer’s Manual
                            </Link>

                      
          
                        <Box component="div" sx={{ mt: 2 }}>
                            {variableGroups.general.map(({ placeholder, label }) => (
                                <Typography key={placeholder} variant="body2">
                                    <strong>{placeholder}</strong> = {label}
                                </Typography>
                            ))}
                        </Box>
                        <Box component="div" sx={{ mt: 2 }}>
                            {variableGroups.extended.map(({ placeholder, label }) => (
                                <Typography key={placeholder} variant="body2">
                                    <strong>{placeholder}</strong> = {label}
                                </Typography>
                            ))}
                        </Box>
                        <Box component="div" sx={{ mt: 2 }}>
                            {variableGroups.pga.map((placeholder) => (
                                <Typography key={placeholder} variant="body2">
                                    <strong>{placeholder}</strong>
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeVariablesDialog}>{t("common.close")}</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default LabelTemplateDialog;
